import { Button, Card, Form, Input, Modal, Select, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { shippingRequestHttp } from "@/api";
import { AIS_SHIP_TYPE } from "@/meta/logistics/shipInfo";
import { PARTNERSHIP } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid,
    Upload as ZhUpload,
    type UploadRequestOptions
} from "zhihao-ui-react";
import { useUserStore } from "@/views/stores";

export interface TransportCapacityValidation {
    ship?: {
        id?: string;
        cnname?: string;
        name?: string;
        type?: number;
        imo?: string;
        callSign?: string;
        deep?: number;
        length?: number;
        width?: number;
        draft?: number;
    };
    owner?: { id?: number; institutionName?: string };
    ownerShip?: { partnership?: number };
}
export interface TransportCapacityPayload {
    mmsi: string;
    ownerId: number;
    partnership: number;
    certificateFiles: {
        shipNationalityCertificateFile: number;
        shipInspectionReportFile: number;
        shipBusinessTransportCertificateFile: number;
        vesselCallingAgreementFile: number;
    };
}
export interface TransportCapacityFormPageProps {
    mmsi?: string;
    ownerId?: number;
    loadValidation?: (params: { mmsi: string; ownerId: number }) => Promise<TransportCapacityValidation>;
    createCapacity?: (payload: TransportCapacityPayload) => Promise<unknown>;
    banButton?: boolean;
}
/** 复用 Vue 新增前置校验接口。 */
export async function loadTransportCapacityValidation(params: { mmsi: string; ownerId: number }) {
    const response = await shippingRequestHttp.service.get("/owner-ship/find/create/validate", { params });
    return response.data as TransportCapacityValidation;
}
/** 复用 Vue owner-ship 新增接口，保持证件 ID 为数字。 */
export async function createTransportCapacity(payload: TransportCapacityPayload) {
    const response = await shippingRequestHttp.service.post("/owner-ship", payload);
    return response.data;
}
const fileFields = [
    ["shipNationalityCertificateFile", "船舶国籍证书"],
    ["shipInspectionReportFile", "船舶校验证书"],
    ["shipBusinessTransportCertificateFile", "船舶营运证"],
    ["vesselCallingAgreementFile", "船舶所有权登记证书"]
] as const;
const CERTIFICATE_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);
const CERTIFICATE_IMAGE_MAX_SIZE = 10 * 1024 * 1024;

/** 对齐 Vue 新增运力页的船舶长宽展示口径。 */
const formatShipDimensions = (length?: number, width?: number) => {
    if (!length && !width) return "--/--";
    const lengthText = length ? `${length}` : "--";
    const widthText = width ? `${width}米` : "--";
    return `${lengthText} | ${widthText}`;
};

/** 对齐 Vue 详情字段的单位展示口径。 */
const formatWithUnit = (value?: number) => (value === undefined || value === null ? "--" : `${value}米`);

/** 物流管理-新增运力，保留 Vue 合作关系、证件文件和 payload 校验。 */
export function TransportCapacityFormPage({
    mmsi,
    ownerId,
    loadValidation = loadTransportCapacityValidation,
    createCapacity = createTransportCapacity,
    banButton
}: TransportCapacityFormPageProps) {
    const params = useParams<{ mmsi?: string; ownerId?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    // 对齐旧 Vue userStore.banButton，同时允许测试/宿主显式传入封禁状态。
    const storeBanButton = useUserStore(state => state.banButton);
    const isButtonBanned = banButton ?? storeBanButton;
    const resolvedMmsi = mmsi ?? searchParams.get("mmsi") ?? params.mmsi ?? "";
    const resolvedOwnerId = ownerId ?? Number(searchParams.get("ownerId") ?? params.ownerId ?? 0);
    const [detail, setDetail] = useState<TransportCapacityValidation>();
    const [partnership, setPartnership] = useState<number>();
    const [files, setFiles] = useState<Record<(typeof fileFields)[number][0], string>>({
        shipNationalityCertificateFile: "",
        shipInspectionReportFile: "",
        shipBusinessTransportCertificateFile: "",
        vesselCallingAgreementFile: ""
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [pendingPayload, setPendingPayload] = useState<TransportCapacityPayload>();
    // 旧 Vue 仅在挂靠/自有关系下展示第四份证件，避免未选择关系时误显示所有权材料。
    const visibleFileFields =
        partnership === PARTNERSHIP.OWN.code || partnership === PARTNERSHIP.AFFILIATION.code
            ? fileFields
            : fileFields.slice(0, 3);
    useEffect(() => {
        let disposed = false;
        void loadValidation({ mmsi: resolvedMmsi, ownerId: resolvedOwnerId })
            .then(result => {
                if (!disposed) setDetail(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("船舶信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadValidation, resolvedMmsi, resolvedOwnerId]);

    /** 复用 Vue upload-img 的证件校验，成功后将文件 ID 回填到现有 payload 字段。 */
    const uploadCertificate = async (
        field: (typeof fileFields)[number][0],
        label: string,
        options: UploadRequestOptions
    ) => {
        const file = options.file;
        if (!file) return;
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (!CERTIFICATE_IMAGE_TYPES.has(file.type) || !["jpg", "jpeg", "png"].includes(extension || "")) {
            setErrorMessage(`${label}仅支持 JPEG、PNG 格式`);
            return;
        }
        if (file.size > CERTIFICATE_IMAGE_MAX_SIZE) {
            setErrorMessage(`${label}大小不能超过10MB`);
            return;
        }
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            // 文件服务只在用户选择证件后加载，避免表单首屏受到上传模块影响。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
            setFiles(current => ({ ...current, [field]: String(fileId) }));
            setErrorMessage("");
            options.onSuccess(uploadedFile);
        } catch {
            setErrorMessage(`${label}上传失败`);
            options.onError(new Error(`${label}上传失败`));
        }
    };
    const save = () => {
        if (!partnership) {
            setErrorMessage("请选择合作关系");
            return;
        }
        const required = [
            "shipNationalityCertificateFile",
            "shipInspectionReportFile",
            "shipBusinessTransportCertificateFile"
        ] as const;
        const missing = required.find(field => !Number(files[field]));
        if (missing) {
            setErrorMessage(`请上传${fileFields.find(item => item[0] === missing)?.[1] || "证件"}`);
            return;
        }
        if (
            (partnership === PARTNERSHIP.OWN.code || partnership === PARTNERSHIP.AFFILIATION.code) &&
            !Number(files.vesselCallingAgreementFile)
        ) {
            setErrorMessage(
                `请上传${partnership === PARTNERSHIP.OWN.code ? "船舶所有权登记证书" : "挂靠协议/租赁协议"}`
            );
            return;
        }
        setErrorMessage("");
        setPendingPayload({
            mmsi: detail?.ship?.id || resolvedMmsi,
            ownerId: detail?.owner?.id || resolvedOwnerId,
            partnership,
            certificateFiles: {
                shipNationalityCertificateFile: Number(files.shipNationalityCertificateFile),
                shipInspectionReportFile: Number(files.shipInspectionReportFile),
                shipBusinessTransportCertificateFile: Number(files.shipBusinessTransportCertificateFile),
                vesselCallingAgreementFile: Number(files.vesselCallingAgreementFile) || 0
            }
        });
    };
    const confirmSave = async () => {
        if (!pendingPayload) return;
        try {
            await createCapacity(pendingPayload);
            message.success("保存成功");
            setPendingPayload(undefined);
            navigate(-1);
        } catch {
            setErrorMessage("保存失败");
        }
    };
    return (
        <section aria-label="transport-capacity-form-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title level={4}>新增船舶</Typography.Title>
                {errorMessage ? <Alert role="alert" type="error" message={errorMessage} /> : null}
                <ZhMultiGrid>
                    <div className="mt-2 rounded-lg bg-white p-4">
                        <div className="mb-3 font-medium">船舶基本信息</div>
                        <ZhGrid cols={4} gap={[16, 20]}>
                            <ZhBaseItem
                                label="中文船名"
                                // 传入节点保持值只渲染一次，避免 beta.13 测试替身的辅助精确值重复显示。
                                value={<>{detail?.ship?.cnname || "--"}</>}
                            />
                            <ZhBaseItem label="英文船名" value={detail?.ship?.name || "--"} />
                            <ZhBaseItem label="类型" value={AIS_SHIP_TYPE.getIntroByCode(detail?.ship?.type)} />
                            <ZhBaseItem label="型深" value={formatWithUnit(detail?.ship?.deep)} />
                            <ZhBaseItem label="IMO" value={detail?.ship?.imo || "--"} />
                            <ZhBaseItem label="呼号" value={detail?.ship?.callSign || "--"} />
                            <ZhBaseItem
                                label="船长/宽"
                                value={formatShipDimensions(detail?.ship?.length, detail?.ship?.width)}
                            />
                            <ZhBaseItem label="吃水" value={formatWithUnit(detail?.ship?.draft)} />
                        </ZhGrid>
                    </div>
                    <div className="mt-2 rounded-lg bg-white p-4">
                        <div className="mb-3 font-medium">绑定信息</div>
                        <ZhGrid cols={4} gap={[16, 20]}>
                            <ZhGridItem>
                                <ZhBaseItem label="货主" value={detail?.owner?.institutionName || "--"} />
                            </ZhGridItem>
                            <ZhGridItem>
                                <Form.Item label="合作关系" required>
                                    <Select
                                        aria-label="合作关系"
                                        className="w-full"
                                        value={partnership ?? ""}
                                        options={PARTNERSHIP.options.map(option => ({
                                            value: option.value,
                                            label: option.label
                                        }))}
                                        placeholder="请选择合作关系"
                                        showSearch
                                        onChange={value => setPartnership(value == null ? undefined : Number(value))}
                                        onSearch={value => {
                                            const numericValue = Number(value);
                                            if (PARTNERSHIP.options.some(option => option.value === numericValue)) {
                                                setPartnership(numericValue);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                        </ZhGrid>
                    </div>
                    <div className="mt-2 rounded-lg bg-white p-4">
                        <div className="mb-3 font-medium">证件信息</div>
                        <ZhGrid cols={4} gap={[16, 20]}>
                            <ZhGridItem span={4}>
                                <div className="flex flex-wrap gap-4">
                                    {visibleFileFields.map(([field, label]) => (
                                        <Form.Item
                                            key={field}
                                            label={
                                                partnership === PARTNERSHIP.AFFILIATION.code &&
                                                field === "vesselCallingAgreementFile"
                                                    ? "挂靠协议/租赁协议"
                                                    : label
                                            }
                                        >
                                            <Input
                                                aria-label={label}
                                                value={files[field]}
                                                onChange={event =>
                                                    setFiles(current => ({
                                                        ...current,
                                                        [field]: event.target.value
                                                    }))
                                                }
                                                placeholder="请输入文件 ID"
                                            />
                                            <ZhUpload
                                                aria-label={`${label}上传`}
                                                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                                action="#"
                                                limit={1}
                                                showFileList={false}
                                                beforeUpload={file => {
                                                    const extension = file.name.split(".").pop()?.toLowerCase();
                                                    if (
                                                        !CERTIFICATE_IMAGE_TYPES.has(file.type) ||
                                                        !["jpg", "jpeg", "png"].includes(extension || "")
                                                    ) {
                                                        setErrorMessage(`${label}仅支持 JPEG、PNG 格式`);
                                                        return false;
                                                    }
                                                    if (file.size > CERTIFICATE_IMAGE_MAX_SIZE) {
                                                        setErrorMessage(`${label}大小不能超过10MB`);
                                                        return false;
                                                    }
                                                    return true;
                                                }}
                                                httpRequest={options => void uploadCertificate(field, label, options)}
                                            >
                                                <Button htmlType="button">上传{label}</Button>
                                            </ZhUpload>
                                        </Form.Item>
                                    ))}
                                </div>
                            </ZhGridItem>
                        </ZhGrid>
                    </div>
                </ZhMultiGrid>
                <Form layout="vertical">
                    <Space className="operation-fixed-btn">
                        <Button aria-label="保存" disabled={isButtonBanned} type="primary" onClick={save}>
                            保存
                        </Button>
                        <Button aria-label="返回" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </Form>
            </Card>
            <Modal
                open={Boolean(pendingPayload)}
                title="保存"
                okText="确认保存"
                cancelText="取消"
                onCancel={() => setPendingPayload(undefined)}
                onOk={() => void confirmSave()}
            >
                确认保存当前运力信息吗？
            </Modal>
        </section>
    );
}

export default TransportCapacityFormPage;
